import getFeaturePlansRecommended from 'core/services/getFeaturePlansRecommended'
import { TEMPORAL_DATA_FROM_QUOTE_KEY } from 'core/constants/storage'
import fetchJSONHandleError, {
  type FetchJSONHandleErrorResponse
} from 'utils/NET/fetchJSONHandleError'
import { getQuoteDate } from 'utils/pivot/date/getLastStartDate'
import { BACKEND_PIVOT } from 'core/constants/endpoints'
import getRecEngine from 'core/services/getRecEngine'
import {
  deepClone,
  stringToDate,
  getDifferenceOnDays,
  propAccessor,
  IDictionary
} from '@hc-frontend/utils'
import sessionData from 'core/sessionData'
import {
  IS_ENROLL,
  IS_AGENT,
  IS_DENTAL_AND_VISION,
  IS_FIXED_BENEFIT_MEDICAL
} from 'core/constants/environment'
import isBTMPlan from 'utils/pivot/plan/isBTMPlan'
import { ADD_ONS_PLANS } from 'core/constants/products'
import { findChildren } from '@hc-frontend/business'
import { CDN_PIVOT } from 'core/constants/endpoints'
import getURLParams from 'utils/DOM/getURLParams'
import getQuoteByKey from '../getQuoteByKey'

function parseMember(member) {
  return {
    dob: member.stringDOB,
    gender: member.gender.toUpperCase(),
    role: member.type.toLowerCase()
  }
}

function makePayload(store, products, plans) {
  const { hcStorage, planId } = store
  const days = hcStorage.endDate
    ? getDifferenceOnDays(
        stringToDate(hcStorage.sdate)!,
        stringToDate(hcStorage.endDate)!
      )
    : 0

  const quoteDate = getQuoteDate()

  const selectedPlanIsBTM = planId && store.plans && isBTMPlan(store.plans[planId])

  let members = hcStorage.members

  if (IS_FIXED_BENEFIT_MEDICAL && hcStorage.childOnly) {
    members = findChildren(hcStorage.members)
    products = products.filter(productId => !ADD_ONS_PLANS.includes(productId))
  }

  return {
    healthcareUserId: hcStorage.healthcareUserId,
    healthcareSession: hcStorage.healthcareSession,
    agentId: '' + hcStorage.agent_id,
    carrierId: 1,
    location: {
      state: hcStorage.location.state_acronym,
      zip: hcStorage.location.zip_code
    },
    coverage: {
      startDate: hcStorage.sdate,
      endDate:
        IS_DENTAL_AND_VISION || days === +hcStorage.period || selectedPlanIsBTM
          ? ''
          : hcStorage.endDate
    },
    members: members.map(parseMember),
    products,
    plans,
    quoteDate: quoteDate ? quoteDate : null,
    multiCarrier: store.eligibilityBool,
    quoteKey: hcStorage.quoteKey,
    email: hcStorage.contact.email_address
  }
}

/**
 * Ensure the recEngine consider the responses from the different sources.
 * The staticRecPlansResponse is used to override the recEngine recommendations when it's available.
 * This is useful since the recEngine service is not available for all application environments.
 *
 * @param store - The current store instance
 * @param recEngineResponse - The fetchJSONHandleError response from the recEngine service
 * @param staticRecPlansResponse - The fetchJSONHandleError response from the static json file with the recommendations
 * @returns
 */

const sanitizeRecEngine = (
  store: Pivot.State,
  recEngineResponse: FetchJSONHandleErrorResponse<Pivot.RecommendationEngine>,
  staticRecPlansResponse: FetchJSONHandleErrorResponse<
    Record<string, Pivot.RecommendationEngine['recommendations']>
  >
) => {
  if (!recEngineResponse && !staticRecPlansResponse) return

  const staticRecommendedPlans =
    staticRecPlansResponse &&
    staticRecPlansResponse[store.hcStorage.location.state_acronym]

  const recEngine = {
    ...recEngineResponse,
    recommendations: staticRecommendedPlans || recEngineResponse.recommendations
  }
  if (staticRecommendedPlans && recEngine.error) delete recEngine.error // remove error from recEngine if we have staticRecommendedPlans

  return recEngine
}

const performNewGetQuoteRequest = ({
  store,
  data,
  temporalData,
  fetchRecEngine,
  fetchAlternativeRecommendedPlans
}) => {
  const services = [
    temporalData
      ? Promise.resolve(temporalData.quoteResponse)
      : fetchJSONHandleError({ url: `${BACKEND_PIVOT}/v2/getQuote`, data }),
    fetchRecEngine
      ? getRecEngine(store.hcStorage)
      : getFeaturePlansRecommended(store.hcStorage),
    fetchAlternativeRecommendedPlans
      ? fetchJSONHandleError({
          url: `${CDN_PIVOT}/resources/v2/products/enroll-recommended-plans.json`
        })
      : undefined
  ].filter(Boolean)
  return Promise.all(services).then(([quoteResponse, recEngine, recommendPlans]) => {
    if (quoteResponse.status === 'ERROR') {
      quoteResponse.hasError = true
      return [quoteResponse, recEngine]
    }

    const response = [
      quoteResponse.data,
      sanitizeRecEngine(store, recEngine, recommendPlans)
    ]

    return response
  })
}

const performReloadQuoteRequest = (store, quoteKey, fetchRecEngine) =>
  Promise.all([
    getQuoteByKey(quoteKey),
    fetchRecEngine
      ? getRecEngine(store.hcStorage)
      : getFeaturePlansRecommended(store.hcStorage),
    fetchJSONHandleError({
      url: `${CDN_PIVOT}/resources/v2/products/enroll-recommended-plans.json`
    })
  ]).then(([quoteResponse, recEngineResponse]) => [
    { hasError: !!quoteResponse.error, ...quoteResponse.data },
    recEngineResponse
  ])

function getPlansByProductId(
  { products = [], plans = [], fetchRecEngine = !IS_AGENT },
  store: Pivot.State
) {
  // If we have a signed pivot state, return the quote from it
  const signedQuote = propAccessor(
    store as unknown as IDictionary<unknown>,
    'signedDetails.quote'
  )
  if (signedQuote) return Promise.resolve(signedQuote)

  // If we have invalid information from quoteKey, return the new start date
  const temporalData = sessionData.getData(TEMPORAL_DATA_FROM_QUOTE_KEY)
  sessionData.setData(TEMPORAL_DATA_FROM_QUOTE_KEY, '')
  if (temporalData && temporalData.nextStartDate) {
    return Promise.resolve([
      {
        hasError: true,
        status: 'ERROR',
        description: 'start date',
        nextStartDate: temporalData.nextStartDate
      }
    ])
  }

  // Create keySession that will identify the quote response
  const data = makePayload(store, products, plans)
  const fetchAlternativeRecommendedPlans = fetchRecEngine
  const payloadValue = deepClone(data)
  const keySession = `Plans-${
    IS_ENROLL ? `RANKING${fetchAlternativeRecommendedPlans ? 1 : 0}` : 'PLANE'
  }-${JSON.stringify(payloadValue)}`

  // May retrieve existing quote from the backend service using the query parameter
  const quoteKey = getURLParams()['quoteKey']
  if (quoteKey)
    return performReloadQuoteRequest(store, quoteKey, fetchRecEngine).then(response => {
      sessionData.setData(keySession, response)
      return response
    })

  // May retrieve existing quote response from sessionData
  const lastResponse = sessionData.getData(keySession)
  if (!temporalData && lastResponse) {
    return Promise.resolve(lastResponse)
  }

  // Perform new getQuote request and save the response in sessionData
  return performNewGetQuoteRequest({
    store,
    data,
    temporalData,
    fetchRecEngine,
    fetchAlternativeRecommendedPlans
  }).then(response => {
    sessionData.setData(keySession, response)
    return response
  })
}

export default getPlansByProductId
